
export type View = 'DASHBOARD' | 'WAYBILLS' | 'DICTIONARIES' | 'WAREHOUSE' | 'REPORTS' | 'ADMIN' | 'ABOUT' | 'USER_GUIDE' | 'ADMIN_GUIDE' | 'DEVELOPER_GUIDE' | 'TESTING_GUIDE' | 'MEDICAL_REPORT' | 'OWNER_PANEL';

export type DictionaryType = 'fuelTypes' | 'organizations' | 'vehicles' | 'employees' | 'storageLocations' | 'routes' | 'calendar' | 'tires' | 'schedule' | 'stockItems';

export enum WaybillStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  POSTED = 'Posted',
  CANCELLED = 'Cancelled',
  COMPLETED = 'Completed'
}

export enum OrganizationStatus {
  ACTIVE = 'Active',
  ARCHIVED = 'Archived',
  LIQUIDATED = 'Liquidated'
}

export enum VehicleStatus {
  ACTIVE = 'Active',
  ARCHIVED = 'Archived',
  MAINTENANCE = 'Maintenance'
}

export type DriverLicenseCategory =
  // Обычные права
  | 'M' | 'A' | 'A1' | 'B' | 'B1' | 'C' | 'C1' | 'D' | 'D1'
  | 'BE' | 'CE' | 'C1E' | 'DE' | 'D1E' | 'Tm' | 'Tb'
  // УТМ (самоходная техника)
  | 'AI' | 'AII' | 'A3' | 'A4' | 'E' | 'F';

export type StockTransactionType = 'income' | 'expense';
export type StockExpenseReason = 'waybill' | 'maintenance' | 'writeOff' | 'fuelCardTopUp' | 'inventoryAdjustment' | 'other';
export type StorageType = 'centralWarehouse' | 'remoteWarehouse' | 'vehicleTank' | 'contractorWarehouse';
export type BlankStatus = 'available' | 'issued' | 'reserved' | 'used' | 'returned' | 'spoiled';
export type SpoilReasonCode = 'damaged' | 'misprint' | 'lost' | 'other';
export type Role = 'admin' | 'user' | 'auditor' | 'driver' | 'mechanic' | 'reviewer' | 'accountant' | 'viewer';
export type Capability = string;
export type EmployeeType = 'driver' | 'dispatcher' | 'mechanic' | 'controller' | 'accountant' | 'manager' | 'other';
export type WaybillCalculationMethod = 'by_total' | 'by_segment';
export type TireSeason = 'Summer' | 'Winter' | 'AllSeason';
export type TireStatus = 'InStock' | 'Mounted' | 'Disposed';
export type StockTransactionStatus = 'Draft' | 'Posted';

export interface Route {
  id: string;
  from: string;
  to: string;
  distanceKm: number;
  isCityDriving: boolean;
  isWarming: boolean;
  isMountainDriving?: boolean;  // COEF-MOUNTAIN-001
  date?: string;
  departureTime?: string;
  arrivalTime?: string;
  notes?: string;
  isRestoredDestination?: boolean; // Flag if 'to' was stitched from next segment
}

export interface Attachment {
  name: string;
  size: number;
  type: string;
  content: string;
  userId: string;
}

export interface Waybill {
  id: string;
  number: string;
  date: string;
  vehicleId: string;
  driverId: string;
  status: WaybillStatus;
  odometerStart: number;
  odometerEnd?: number;
  fuelAtStart: number;
  fuelFilled?: number;
  fuelAtEnd?: number;
  fuelPlanned?: number;
  routes: Route[];
  organizationId: string;
  dispatcherId?: string;
  controllerId?: string;
  reviewerId?: string;
  validFrom: string;
  validTo: string;
  attachments?: Attachment[];
  reviewerComment?: string;
  deviationReason?: string;
  calculationMethod?: WaybillCalculationMethod;
  customFuelRate?: number;  // Ручная норма расхода (л/100км), переопределяет автоматический расчёт
  blankId?: string | null;
  blankSeries?: string | null;
  blankNumber?: number | null;
  postedAt?: string;
  postedBy?: string;
  linkedStockTransactionIds?: string[];
  notes?: string;
  assignedDriverUid?: string; // Firebase UID for cloud pairing
}

export interface Vehicle {
  id: string;
  plateNumber: string;
  brand: string;
  vin?: string | null;
  vehicleCategory?: DriverLicenseCategory | null;
  bodyNumber?: string | null;
  chassisNumber?: string | null;
  mileage: number;
  engineHours?: number | null;  // Моточасы (для спецтехники)
  currentFuel?: number;
  fuelTypeId?: string;
  assignedDriverId?: string | null;
  assignedDriverUids?: string[]; // Firebase UIDs for security rules
  organizationId?: string | null;
  status: VehicleStatus;
  fuelConsumptionRates: {
    summerRate: number;
    winterRate: number;
    cityIncreasePercent?: number;
    warmingIncreasePercent?: number;
    mountainIncreasePercent?: number;  // COEF-MOUNTAIN-001
  };
  useCityModifier?: boolean;
  useWarmingModifier?: boolean;
  useMountainModifier?: boolean;  // COEF-MOUNTAIN-001
  fuelTankCapacity?: number | null;
  disableFuelCapacityCheck?: boolean;
  // ОСАГО
  osagoSeries?: string | null;
  osagoNumber?: string | null;
  osagoStartDate?: string | null;
  osagoEndDate?: string | null;
  // СТС
  stsSeries?: string | null;
  stsNumber?: string | null;
  stsIssueDate?: string | null;
  // ПТС/ЭПТС или ПСМ/ЭПСМ (для спецтехники)
  ptsType?: 'PTS' | 'EPTS' | 'PSM' | 'EPSM' | null;
  ptsSeries?: string | null;
  ptsNumber?: string | null;
  eptsNumber?: string | null;
  // Диагностическая карта (для обычных ТС, формат номера: 15 цифр)
  diagnosticCardNumber?: string | null;
  diagnosticCardIssueDate?: string | null;
  diagnosticCardExpiryDate?: string | null;
  // Для спецтехники: Св-во ТО или Акт ТО (вместо ДК)
  inspectionDocType?: 'SVTO' | 'AKTTO' | null;  // Св-во ТО или Акт ТО
  // Св-во ТО: серия (2 буквы) + номер (6 цифр), срок 1 год
  svtoSeries?: string | null;
  svtoNumber?: string | null;
  svtoIssueDate?: string | null;
  svtoExpiryDate?: string | null;
  // Акт ТО: номер (6 цифр), срок 20 дней
  akttoNumber?: string | null;
  akttoDate?: string | null;
  akttoExpiryDate?: string | null;
  maintenanceIntervalKm?: number | null;
  lastMaintenanceMileage?: number | null;
  year?: number | null;
  vehicleType?: string | null;
  notes?: string | null;
  maintenanceHistory?: any[]; // Simplified
  sts?: any; // Encrypted/Decrypted STS object container
  pts?: any; // Encrypted/Decrypted PTS/EPTS object container
}

export interface Employee {
  id: string;
  firebaseUid?: string; // Link to Firebase Auth
  fullName: string;
  shortName: string;
  employeeType: EmployeeType;
  position?: string;
  positionShort?: string;
  organizationId?: string | null;
  status: 'Active' | 'Inactive';
  fuelCardBalance?: number;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  dateOfBirth?: string | null;
  notes?: string | null;
  snils?: string | null;
  personnelNumber?: string | null;
  licenseCategory?: string;
  documentNumber?: string | null;
  documentExpiry?: string | null;
  fuelCardNumber?: string | null;
  medicalCertificateSeries?: string | null;
  medicalCertificateNumber?: string | null;
  medicalCertificateIssueDate?: string | null;
  medicalCertificateExpiryDate?: string | null;
  medicalInstitutionId?: string;
  blankBatches?: string[];
  dispatcherId?: string;
  controllerId?: string;
  reviewerId?: string;
  passport?: any; // Encrypted/Decrypted Passport object container
}

export interface Organization {
  id: string;
  shortName: string;
  fullName?: string;
  status: OrganizationStatus;
  inn?: string | null;
  kpp?: string | null;
  ogrn?: string | null;
  address?: string | null;
  postalAddress?: string | null;
  contactPerson?: string | null;
  phone?: string | null;
  email?: string | null;
  bankAccount?: string | null;
  correspondentAccount?: string | null;
  bankName?: string | null;
  bankBik?: string | null;
  accountCurrency?: string | null;
  paymentPurpose?: string | null;
  group?: string | null;
  notes?: string | null;
  medicalLicenseNumber?: string | null;
  medicalLicenseIssueDate?: string | null;
  parentOrganizationId?: string | null;
  isOwn?: boolean;
  oktmo?: string | null;
  okpo?: string | null;
  registrationDate?: string | null;
  pairingCode?: string; // Short code for driver pairing (e.g. ORG-123)
}

export interface FuelType {
  id: string;
  name: string;
  code: string;
  density?: number;
}

export interface SavedRoute {
  id: string;
  from: string;
  to: string;
  distanceKm: number;
}

export interface GarageStockItem {
  id: string;
  name: string;
  itemType?: string;
  group: string;
  unit: string;
  balance: number;
  code: string;
  storageLocation?: string;
  notes?: string;
  balanceAccount?: string;
  budgetCode?: string;
  isFuel?: boolean;
  fuelTypeId?: string;
  isActive: boolean;
  organizationId?: string;
  lastPurchasePrice?: number;
  lastTransactionDate?: string;
}

export interface StockTransaction {
  id: string;
  docNumber: string;
  date: string;
  type: StockTransactionType;
  status?: StockTransactionStatus;
  items: { stockItemId: string; quantity: number; unitPrice?: number; serialNumber?: string }[];
  vehicleId?: string;
  driverId?: string;
  supplier?: string;
  notes?: string;
  organizationId: string;
  expenseReason?: StockExpenseReason;
  supplierOrganizationId?: string;
  waybillId?: string | null;
}

export interface FuelCardSchedule {
  id: string;
  driverId: string;
  stockItemId: string;
  quantity: number;
  frequency: 'monthly' | 'quarterly';
  isActive: boolean;
  notes?: string;
  lastExecutedAt?: string;
}

export interface CalendarEvent {
  id: string;
  date: string;
  type: 'workday' | 'holiday' | 'short';
  note?: string;
}

export interface DashboardWidgetsSettings {
  showStatuses: boolean;
  showFleetStats: boolean;
  showCharts: boolean;
  showOverruns: boolean;
  showMaintenance: boolean;
  showBirthdays: boolean;
}

export interface AppSettings {
  isParserEnabled: boolean;
  appMode: 'driver' | 'central';
  autoSaveRoutes?: boolean;
  customLogo?: string | null;
  blanks?: {
    driverCanAddBatches?: boolean;
  };
  dashboardWidgets?: DashboardWidgetsSettings;
  enableWarehouseAccounting?: boolean;

  tireDepreciationMethod?: 'usage' | 'seasonal';
  enableWebvisorPrivacyMasking?: boolean; // New setting
}

export interface PeriodLock {
  id: string;
  period: string;
  lockedAt: string;
  lockedByUserId: string;
  dataHash: string;
  recordCount: number;
  notes?: string;
}

export interface WaybillBlankBatch {
  id: string;
  organizationId: string;
  series: string;
  startNumber: number;
  endNumber: number;
  status: string;
  notes?: string;
  isMaterialized?: boolean;
  issuedCount?: number;
  totalCount?: number;
}

export interface WaybillBlank {
  id: string;
  organizationId: string;
  batchId: string;
  series: string;
  number: number;
  status: BlankStatus;
  ownerEmployeeId?: string | null;
  version?: number;
  updatedAt?: string;
  updatedBy?: string;
  spoilReasonCode?: SpoilReasonCode;
  spoilReasonNote?: string;
  spoiledAt?: string;
  reservedByWaybillId?: string | null;
  reservedAt?: string | null;
  usedInWaybillId?: string | null;
}

export interface Tire {
  id: string;
  stockItemId?: string;
  quantity?: number;
  brand: string;
  model: string;
  size: string;
  season: TireSeason;
  status: TireStatus;
  condition: string;
  currentVehicleId?: string | null;
  storageLocationId?: string | null;
  purchaseDate?: string | null;
  purchasePrice?: number | null;
  startDepth?: number | null;
  currentDepth?: number | null;
  installDate?: string | null;
  installOdometer?: number | null;
  estimatedLifespanKm?: number | null;
  disposalDate?: string | null;
  utilizationDate?: string | null;
  notes?: string | null;
  summerMileage?: number;
  winterMileage?: number;
}

export interface User {
  id: string;
  displayName: string;
  role: Role;
  extraCaps?: Capability[];
  email?: string;
  firebaseUid?: string; // Firebase Auth UID for pairing
}

export interface BalanceSnapshot {
  id: string;
  driverId: string;
  date: string;
  balance: number;
  createdAt: string;
}

export interface PrintPositions {
  [key: string]: { x: number; y: number; width?: number; height?: number };
}

export interface KpiData {
  totalMileage: number;
  totalFuel: number;
  totalFuelCardBalance: number;
  issues: number;
  fuelMonth: number;
  fuelQuarter: number;
  fuelYear: number;
}

export interface SeasonSettings {
  type: 'recurring' | 'manual';
  summerDay?: number;
  summerMonth?: number;
  winterDay?: number;
  winterMonth?: number;
  winterStartDate?: string;
  winterEndDate?: string;
}

export interface StorageLocation {
  id: string;
  name: string;
  type: StorageType;
  organizationId: string;
  address?: string | null;
  responsiblePerson?: string | null;
  description?: string | null;
  status: 'active' | 'archived';
}

export const EMPLOYEE_TYPE_TRANSLATIONS: Record<EmployeeType, string> = {
  driver: 'Водитель',
  dispatcher: 'Диспетчер',
  mechanic: 'Механик',
  controller: 'Контролер',
  accountant: 'Бухгалтер',
  manager: 'Менеджер',
  other: 'Другое'
};

export type StockMovementType = 'INCOME' | 'EXPENSE' | 'ADJUSTMENT' | 'TRANSFER';
export type StockLocationType = 'WAREHOUSE' | 'FUEL_CARD' | 'VEHICLE_TANK';

export interface StockLocation {
  id: string;
  organizationId: string;
  type: StockLocationType;
  name: string;
  vehicleId?: string;
  fuelCardId?: string;
}

export interface StockMovement {
  id: string;
  organizationId: string;
  externalRef: string;
  occurredAt: string;
  type: StockMovementType;
  stockItemId: string;
  quantity: number;
  stockLocationId?: string;
  fromStockLocationId?: string;
  toStockLocationId?: string;
  documentType: string;
  documentId: string;
  isVoid: boolean;
  notes?: string;
  createdByUserId?: string;
}

export interface FuelCard {
  id: string;
  organizationId: string;
  cardNumber: string;
  driverId?: string; // Currently assigned driver
  vehicleId?: string; // Future use
  stockLocationId: string; // The virtual container for this card's fuel
  isActive: boolean;
  notes?: string;
}


export interface DriverRefueling {
    id: string;
    organizationId: string;
    vehicleId: string;
    driverId: string;
    date: string; // ISO
    amountLiters: number;
    pricePerLiter?: number;
    totalCost?: number;
    odometer: number;
    attachmentUrl?: string; // URL to Firebase Storage photo (full size)
    receiptThumbUrl?: string; // URL to Firebase Storage thumbnail
    fuelType?: string; // Тип топлива (АИ-92, АИ-95, ДТ, etc.)
    supplierName?: string; // Наименование АЗС/поставщика (из AI-парсинга)
    status: 'pending' | 'integrated' | 'rejected';
    notes?: string;
    createdAt: string;
    processedAt?: string;
    processedBy?: string;
}
